import { useEffect, useState } from 'react';
import { Box, Center, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { Pagination } from '@themes/aegix';
import { useEventLogs } from '../../../hooks/queries/useReunification';
import type { EventAssignmentLog } from '../../../types/ReunificationTypes';

function LogEntry({ log }: { log: EventAssignmentLog }) {
  return (
    <Box
      p={4}
      bg="bg.subtle"
      borderRadius="md"
      border="1px solid"
      borderColor="border.subtle"
    >
      <HStack justify="space-between" mb={1}>
        <Text fontSize="sm" fontWeight="600" data-e2e="event-audit-log-action">
          {log.action}
        </Text>
        <Text fontSize="xs" color="text.secondary" data-e2e="event-audit-log-timestamp">
          {new Date(log.timestamp).toLocaleString()}
        </Text>
      </HStack>
      <Text fontSize="sm" color="text.secondary" data-e2e="event-audit-log-entity">
        {log.entity_type} {log.entity_id ? `(${log.entity_id})` : ''}
      </Text>
      {(log.old_value || log.new_value) && (
        <Text fontSize="xs" color="text.secondary" mt={1} data-e2e="event-audit-log-old-value">
          {log.old_value ? `From: ${log.old_value}` : ''}
          {log.old_value && log.new_value ? ' → ' : ''}
          {log.new_value ? `To: ${log.new_value}` : ''}
        </Text>
      )}
      <HStack mt={2} justify="space-between">
        <Text fontSize="xs" color="text.secondary" data-e2e="event-audit-log-performed-by">
          By: {log.performed_by_name}
        </Text>
        {log.notes && (
          <Text fontSize="xs" color="text.secondary" fontStyle="italic" data-e2e="event-audit-log-notes">
            {log.notes}
          </Text>
        )}
      </HStack>
    </Box>
  );
}

interface EventAuditLogPanelProps {
  eventId: string;
}

export function EventAuditLogPanel({ eventId }: EventAuditLogPanelProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Backend `audit_log` is DRF-paginated ({count, next, previous, results}).
  // Reading it as a flat array is what threw `logs.map is not a function`.
  const { data, isLoading, error } = useEventLogs(eventId, {
    page: currentPage,
    page_size: pageSize,
  });

  const logs = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize, eventId]);

  return (
    <Box py={2}>
      {isLoading ? (
        <Center py={12}>
          <Spinner size="lg" color="button.primary" />
        </Center>
      ) : error ? (
        <Center py={12}>
          <Text color="text.danger">
            {error instanceof Error ? error.message : 'Failed to load audit log'}
          </Text>
        </Center>
      ) : logs.length === 0 ? (
        <Center py={12}>
          <Text color="text.secondary">No audit log entries</Text>
        </Center>
      ) : (
        <>
          <VStack align="stretch" gap={3}>
            {logs.map((log) => (
              <LogEntry key={log.public_id} log={log} />
            ))}
          </VStack>
          {totalPages > 1 && (
            <Box mt={4}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
